
import React, { useState, useEffect, useCallback } from 'react';
import { Folder, FileText, Download, Trash2, RefreshCw, HardDrive, Clock, Search } from 'lucide-react';
import { useNotification } from '../context/NotificationContext';

const StorageDashboard = ({ api }) => {
    const { notify, confirm } = useNotification();
    const [files, setFiles] = useState({ Reports: [], Predictions: [] });
    const [activeCategory, setActiveCategory] = useState('Reports');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const loadFiles = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await api.fetchStorageFiles();
            setFiles(data);
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
            setError(`Failed to load files: ${detail}`);
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [api.fetchStorageFiles]);

    useEffect(() => {
        loadFiles();
    }, [loadFiles]);

    const handleDelete = async (filePath) => {
        const displayName = filePath.split('/').pop();
        const ok = await confirm({
            title: 'Delete File',
            message: `Are you sure you want to delete ${displayName}?`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            tone: 'danger'
        });
        if (!ok) return;

        try {
            await api.deleteFromStorage(filePath);
            loadFiles();
        } catch (err) {
            notify({ type: 'error', title: 'Delete failed', message: 'Failed to delete file.' });
        }
    };

    const handleDownload = async (filePath) => {
        try {
            await api.downloadFromStorage(filePath);
        } catch (err) {
            notify({ type: 'error', title: 'Download failed', message: 'Failed to download file. Please try again.' });
        }
    };

    const currentFiles = files[activeCategory] || [];
    const filteredFiles = currentFiles.filter(file =>
        file.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', hour12: true });
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="rounded-2xl p-6 shadow-sm border" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center">
                        <div className="p-3 bg-blue-50 rounded-xl mr-4">
                            <HardDrive className="w-8 h-8 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold" style={{ color: 'var(--accent-text)' }}>Storage Hub</h2>
                            <p className="mt-1" style={{ color: 'var(--accent-text-subtle)' }}>Manage exported reports and predictions</p>
                            <p className="mt-1 text-xs" style={{ color: 'var(--accent-text-subtle)' }}>
                                Files are scoped to your signed-in account.
                            </p>
                        </div>
                    </div>
                <button
                    onClick={loadFiles}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl transition-colors shadow-sm text-sm font-medium disabled:opacity-50"
                    style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', color: 'var(--accent-text)' }}
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="rounded-2xl border shadow-sm overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                {/* Tabs & Search */}
                <div className="p-4 border-b flex flex-col md:flex-row gap-4 justify-between items-center" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--card-border)' }}>
                    <div className="flex p-1 rounded-xl w-full md:w-auto" style={{ background: 'var(--bg-secondary)' }}>
                        {['Reports', 'Predictions'].map(cat => (
                            <button
                                key={cat}
                                onClick={() => setActiveCategory(cat)}
                                className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-medium transition-all ${activeCategory === cat
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                <Folder className={`w-4 h-4 ${activeCategory === cat ? 'fill-blue-100' : ''}`} />
                                {cat}
                                <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] ${activeCategory === cat ? 'bg-blue-50 text-blue-600' : 'bg-gray-200 text-gray-500'
                                    }`}>
                                    {files[cat]?.length || 0}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="relative w-full md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search files..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--card-border)', color: 'var(--accent-text)' }}
                        />
                    </div>
                </div>

                {/* File List */}
                <div className="min-h-[400px]">
                    {loading && !currentFiles.length ? (
                        <div className="flex items-center justify-center h-64 text-gray-400">
                            <RefreshCw className="w-6 h-6 animate-spin mr-2" />
                            Loading files...
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center h-64 text-red-500">
                            {error}
                        </div>
                    ) : filteredFiles.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: 'var(--bg-secondary)' }}>
                                <Folder className="w-8 h-8 text-gray-300" />
                            </div>
                            <p>No files found in {activeCategory}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            {/* Helper to group files */}
                            {(() => {
                                const grouped = filteredFiles.reduce((acc, file) => {
                                    const folder = file.folder || 'Root';
                                    if (!acc[folder]) acc[folder] = [];
                                    acc[folder].push(file);
                                    return acc;
                                }, {});

                                return Object.entries(grouped).sort(([a], [b]) => a === 'Root' ? -1 : a.localeCompare(b)).map(([folderName, folderFiles]) => (
                                    <div key={folderName} className="mb-6 last:mb-0">
                                        {folderName !== 'Root' && (
                                            <div className="px-6 py-2 border-y flex items-center gap-2 font-medium text-gray-600 text-sm" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--card-border)' }}>
                                                <Folder className="w-4 h-4 text-blue-400" />
                                                {folderName}
                                                <span className="text-gray-400 text-xs">({folderFiles.length})</span>
                                            </div>
                                        )}
                                        <table className="w-full text-left border-collapse">
                                            {folderName === 'Root' && (
                                                <thead>
                                                    <tr className="border-b text-xs font-semibold text-gray-400 uppercase tracking-wider" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--card-border)' }}>
                                                        <th className="px-6 py-4">Name</th>
                                                        <th className="px-6 py-4">Size</th>
                                                        <th className="px-6 py-4">Date Added</th>
                                                        <th className="px-6 py-4 text-right">Actions</th>
                                                    </tr>
                                                </thead>
                                            )}
                                            <tbody className="divide-y divide-gray-50">
                                                {folderFiles.map((file) => (
                                                    <tr key={file.path} className="group hover:bg-blue-50/5 transition-colors">
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center gap-3">
                                                                <div className="p-2 bg-gray-100 rounded-lg text-gray-500 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                                                                    <FileText className="w-5 h-5" />
                                                                </div>
                                                                <span className="font-medium text-gray-700 group-hover:text-gray-900">
                                                                    {file.original_name || file.name}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                                                            {formatSize(file.size)}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-500">
                                                            <div className="flex items-center gap-2">
                                                                <Clock className="w-3.5 h-3.5 text-gray-400" />
                                                                {formatDate(file.created)}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-right">
                                                            <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button
                                                                    onClick={() => handleDownload(file.path)}
                                                                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                    title="Download"
                                                                >
                                                                    <Download className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(file.path)}
                                                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                    title="Delete"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ));
                            })()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StorageDashboard;
